import { Howl, Howler } from 'howler';
import useSettingsStore from '~/stores/settings';
import { ensureHowlerAudioContext, resumeHowlerAudioContext, safeHowlPlay } from '~/utils/sound';

import alarm from '../mp3/alarm.mp3';
import chips from '../mp3/chips.mp3';
import deal from '../mp3/deal.mp3';
import lightning from '../mp3/lightning.mp3';
import toDealer from '../mp3/to_dealer.mp3';
import toWinner from '../mp3/to_winner.mp3';

import zhMaleCall01 from '../mp3/zh/male/call_01.mp3';
import zhMaleCall02 from '../mp3/zh/male/call_02.mp3';
import zhMaleCall03 from '../mp3/zh/male/call_03.mp3';
import zhMaleCompare from '../mp3/zh/male/compare.mp3';
import zhMaleFold01 from '../mp3/zh/male/fold_01.mp3';
import zhMaleFold02 from '../mp3/zh/male/fold_02.mp3';
import zhMaleFold03 from '../mp3/zh/male/fold_03.mp3';
import zhMaleFold04 from '../mp3/zh/male/fold_04.mp3';
import zhMaleFold05 from '../mp3/zh/male/fold_05.mp3';
import zhMaleRaise01 from '../mp3/zh/male/raise_01.mp3';
import zhMaleRaise02 from '../mp3/zh/male/raise_02.mp3';
import zhMaleRaise03 from '../mp3/zh/male/raise_03.mp3';
import zhMaleRaise04 from '../mp3/zh/male/raise_04.mp3';
import zhMaleSee01 from '../mp3/zh/male/see_01.mp3';
import zhMaleSee02 from '../mp3/zh/male/see_02.mp3';
import zhMaleSee03 from '../mp3/zh/male/see_03.mp3';
import zhMaleCallLast from '../mp3/zh/male/callLast.mp3';

import zhFemaleCall01 from '../mp3/zh/female/call_01.mp3';
import zhFemaleCall02 from '../mp3/zh/female/call_02.mp3';
import zhFemaleCall03 from '../mp3/zh/female/call_03.mp3';
import zhFemaleCompare from '../mp3/zh/female/compare.mp3';
import zhFemaleFold01 from '../mp3/zh/female/fold_01.mp3';
import zhFemaleFold02 from '../mp3/zh/female/fold_02.mp3';
import zhFemaleFold03 from '../mp3/zh/female/fold_03.mp3';
import zhFemaleFold04 from '../mp3/zh/female/fold_04.mp3';
import zhFemaleFold05 from '../mp3/zh/female/fold_05.mp3';
import zhFemaleRaise01 from '../mp3/zh/female/raise_01.mp3';
import zhFemaleRaise02 from '../mp3/zh/female/raise_02.mp3';
import zhFemaleRaise03 from '../mp3/zh/female/raise_03.mp3';
import zhFemaleRaise04 from '../mp3/zh/female/raise_04.mp3';
import zhFemaleSee01 from '../mp3/zh/female/see_01.mp3';
import zhFemaleSee02 from '../mp3/zh/female/see_02.mp3';
import zhFemaleSee03 from '../mp3/zh/female/see_03.mp3';
import zhFemaleCallLast from '../mp3/zh/female/callLast.mp3';

import enMaleCall01 from '../mp3/en/male/call_01.mp3';
import enMaleCall02 from '../mp3/en/male/call_02.mp3';
import enMaleCompare from '../mp3/en/male/compare.mp3';
import enMaleFold01 from '../mp3/en/male/fold_01.mp3';
import enMaleFold02 from '../mp3/en/male/fold_02.mp3';
import enMaleFold03 from '../mp3/en/male/fold_03.mp3';
import enMaleFold04 from '../mp3/en/male/fold_04.mp3';
import enMaleRaise01 from '../mp3/en/male/raise_01.mp3';
import enMaleRaise02 from '../mp3/en/male/raise_02.mp3';
import enMaleRaise03 from '../mp3/en/male/raise_03.mp3';
import enMaleRaise04 from '../mp3/en/male/raise_04.mp3';
import enMaleSee01 from '../mp3/en/male/see_01.mp3';
import enMaleSee02 from '../mp3/en/male/see_02.mp3';
import enMaleSee03 from '../mp3/en/male/see_03.mp3';
import enMaleCallLast from '../mp3/en/male/callLast.mp3';

import enFemaleCall01 from '../mp3/en/female/call_01.mp3';
import enFemaleCall02 from '../mp3/en/female/call_02.mp3';
import enFemaleCompare from '../mp3/en/female/compare.mp3';
import enFemaleFold01 from '../mp3/en/female/fold_01.mp3';
import enFemaleFold02 from '../mp3/en/female/fold_02.mp3';
import enFemaleFold03 from '../mp3/en/female/fold_03.mp3';
import enFemaleFold04 from '../mp3/en/female/fold_04.mp3';
import enFemaleRaise01 from '../mp3/en/female/raise_01.mp3';
import enFemaleRaise02 from '../mp3/en/female/raise_02.mp3';
import enFemaleRaise03 from '../mp3/en/female/raise_03.mp3';
import enFemaleRaise04 from '../mp3/en/female/raise_04.mp3';
import enFemaleSee01 from '../mp3/en/female/see_01.mp3';
import enFemaleSee02 from '../mp3/en/female/see_02.mp3';
import enFemaleSee03 from '../mp3/en/female/see_03.mp3';
import enFemaleCallLast from '../mp3/en/female/callLast.mp3';

type VoiceType = 'call' | 'compare' | 'fold' | 'raise' | 'see' | 'callLast';
type BaseType = 'alarm' | 'chips' | 'deal' | 'lightning' | 'to_dealer' | 'to_winner';

const baseAudioMap: Record<BaseType, string> = {
    alarm,
    chips,
    deal,
    lightning,
    to_dealer: toDealer,
    to_winner: toWinner,
};

const voiceAudioMap = {
    zh: {
        male: {
            call: [zhMaleCall01, zhMaleCall02, zhMaleCall03],
            compare: [zhMaleCompare],
            fold: [zhMaleFold01, zhMaleFold02, zhMaleFold03, zhMaleFold04, zhMaleFold05],
            raise: [zhMaleRaise01, zhMaleRaise02, zhMaleRaise03, zhMaleRaise04],
            see: [zhMaleSee01, zhMaleSee02, zhMaleSee03],
            callLast: [zhMaleCallLast],
        },
        female: {
            call: [zhFemaleCall01, zhFemaleCall02, zhFemaleCall03],
            compare: [zhFemaleCompare],
            fold: [zhFemaleFold01, zhFemaleFold02, zhFemaleFold03, zhFemaleFold04, zhFemaleFold05],
            raise: [zhFemaleRaise01, zhFemaleRaise02, zhFemaleRaise03, zhFemaleRaise04],
            see: [zhFemaleSee01, zhFemaleSee02, zhFemaleSee03],
            callLast: [zhFemaleCallLast],
        },
    },
    en: {
        male: {
            call: [enMaleCall01, enMaleCall02],
            compare: [enMaleCompare],
            fold: [enMaleFold01, enMaleFold02, enMaleFold03, enMaleFold04],
            raise: [enMaleRaise01, enMaleRaise02, enMaleRaise03, enMaleRaise04],
            see: [enMaleSee01, enMaleSee02, enMaleSee03],
            callLast: [enMaleCallLast],
        },
        female: {
            call: [enFemaleCall01, enFemaleCall02],
            compare: [enFemaleCompare],
            fold: [enFemaleFold01, enFemaleFold02, enFemaleFold03, enFemaleFold04],
            raise: [enFemaleRaise01, enFemaleRaise02, enFemaleRaise03, enFemaleRaise04],
            see: [enFemaleSee01, enFemaleSee02, enFemaleSee03],
            callLast: [enFemaleCallLast],
        },
    },
};

const audioCache = new Map<string, Howl>();

/** 正在加载且已挂过「加载完即播放」的 cacheKey，避免同一音频在 loading 时被多次 safePlay 导致播两遍 */
const loadingPlayPending = new Set<string>();

// 音频上下文是否已解锁
let isAudioUnlocked = false;

const pickRandom = (list: string[]) => list[Math.floor(Math.random() * list.length)];

const getLocaleKey = (localeValue?: string) => {
    if (!localeValue) return 'zh';
    if (localeValue === 'cn' || localeValue.startsWith('zh')) return 'zh';
    if (localeValue.startsWith('en')) return 'en';
    return 'zh';
};

const normalizeGender = (gender?: 'male' | 'female' | number): 'male' | 'female' => {
    if (gender === 'female' || gender === 1) return 'female';
    return 'male';
};

/**
 * 解锁音频上下文 - 需要在用户交互时调用
 * 现代浏览器要求用户交互后才能播放音频
 */
export const unlockAudio = () => {
    if (isAudioUnlocked) return;

    ensureHowlerAudioContext();
    const ctx = Howler.ctx as AudioContext | null;
    if (ctx && (ctx.state === 'suspended' || ctx.state === 'interrupted')) {
        resumeHowlerAudioContext().then((ok) => {
            if (ok) {
                isAudioUnlocked = true;
                console.log('[Music] AudioContext resumed');
            }
        });
    } else if (ctx) {
        isAudioUnlocked = true;
    }
};

/**
 * 安全播放音频
 * 处理加载状态和播放错误。loading 时同一 cacheKey 只挂一次「加载完即播放」，避免重复调用导致播两遍
 */
const safePlay = (audio: Howl, cacheKey: string) => {
    ensureHowlerAudioContext();
    resumeHowlerAudioContext();

    const state = audio.state();

    if (state === 'loaded') {
        loadingPlayPending.delete(cacheKey);
        try {
            safeHowlPlay(audio);
        } catch (err) {
            console.warn(`[Music] Play failed for ${cacheKey}:`, err);
        }
    } else if (state === 'loading') {
        if (loadingPlayPending.has(cacheKey)) return; // 已挂过加载完播放，避免重复挂导致播两遍
        loadingPlayPending.add(cacheKey);
        audio.once('load', () => {
            loadingPlayPending.delete(cacheKey);
            try {
                safeHowlPlay(audio);
            } catch (err) {
                console.warn(`[Music] Play failed after load for ${cacheKey}:`, err);
            }
        });
        audio.once('loaderror', (_id, err) => {
            loadingPlayPending.delete(cacheKey);
            console.warn(`[Music] Load failed for ${cacheKey}:`, err);
        });
    } else {
        loadingPlayPending.delete(cacheKey);
        audio.load();
        loadingPlayPending.add(cacheKey);
        audio.once('load', () => {
            loadingPlayPending.delete(cacheKey);
            try {
                safeHowlPlay(audio);
            } catch (err) {
                console.warn(`[Music] Play failed after reload for ${cacheKey}:`, err);
            }
        });
    }
};

const getAudio = (cacheKey: string, src: string, volume: number) => {
    if (!audioCache.has(cacheKey)) {
        const howl = new Howl({
            src: [src],
            html5: false, // 使用 Web Audio API 更可靠
            preload: true,
            volume,
            onloaderror: (_id, err) => {
                console.warn(`[Music] Load error for ${cacheKey}:`, err);
            }
        });

        // 监听播放错误，尝试恢复音频上下文后重新播放
        howl.on('playerror', (_id, err) => {
            console.warn(`[Music] Play error for ${cacheKey}:`, err);
            resumeHowlerAudioContext().then((ok) => {
                if (ok) safeHowlPlay(howl);
            });
        });

        audioCache.set(cacheKey, howl);
    }

    return audioCache.get(cacheKey)!;
};

const stopAllAudio = () => {
    audioCache.forEach(audio => audio.stop());
};

/**
 * 清除所有炸金花相关声音：静音并停止播放，避免页面刷新或切换时未播完的声音残留
 * 应在 onBeforeUnmount 时调用
 */
export const clearAllZhajinhuaSounds = () => {
    audioCache.forEach((audio) => {
        try {
            audio.mute(true);
            audio.stop();
            ensureHowlerAudioContext();
            audio.unload();
        } catch (e) {
            // 忽略报错
        }
    });
    audioCache.clear();
    loadingPlayPending.clear();
    isPreloaded = false; // 重置预加载标记，下次进入房间时可再次预加载
};

// 预加载状态标记
let isPreloaded = false;

/**
 * 预加载所有音频资源（内部使用默认音量）
 * 在模块加载时自动执行
 */
const doPreload = () => {
    if (isPreloaded) return;
    isPreloaded = true;

    const defaultVolume = 1;

    // 预加载基础音效
    Object.entries(baseAudioMap).forEach(([type, src]) => {
        const cacheKey = `base_${type}`;
        getAudio(cacheKey, src, defaultVolume);
    });

    // 预加载语音音效（按语言与性别遍历，避免新语言漏预加载）
    Object.entries(voiceAudioMap).forEach(([locale, genderMap]) => {
        Object.entries(genderMap).forEach(([gender, typeMap]) => {
            Object.entries(typeMap).forEach(([type, srcList]) => {
                srcList.forEach((src, index) => {
                    const cacheKey = `voice_${locale}_${gender}_${type}_${index}`;
                    getAudio(cacheKey, src, defaultVolume);
                });
            });
        });
    });

    console.log('[Music] All audio preloaded');
};

// 模块加载时自动预加载（仅客户端）
if (typeof window !== 'undefined') {
    // 使用 requestIdleCallback 或 setTimeout 在空闲时预加载，不阻塞主线程
    if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => doPreload(), { timeout: 2000 });
    } else {
        setTimeout(doPreload, 100);
    }
}

// 播放对应音效
/**
 *  xx_01, xx_02 多种种的，随机播放对应的一个
 * @param type 
 * @param gender
 */
export const playMusic = (type: BaseType | VoiceType, gender?: 'male' | 'female' | number) => {
    const settingStore = useSettingsStore();
    const { locale } = useNuxtApp().$i18n;
    const { sound } = settingStore.inhouseGames;

    if (!sound.on) {
        stopAllAudio();
        return;
    }

    const localeValue = (locale as { value?: string })?.value ?? (locale as string);
    const localeKey = getLocaleKey(localeValue);

    if (type in baseAudioMap) {
        const src = baseAudioMap[type as BaseType];
        const cacheKey = `base_${type}`;
        const audio = getAudio(cacheKey, src, sound.effects);
        audio.volume(sound.effects);
        safePlay(audio, cacheKey);
        return;
    }

    const voiceLocale = voiceAudioMap[localeKey] ?? voiceAudioMap.zh;
    const normalizedGender = normalizeGender(gender);
    const voiceList = voiceLocale[normalizedGender]?.[type as VoiceType];

    if (!voiceList || voiceList.length === 0) return;

    // 使用索引作为缓存键，避免因路径不同导致重复创建实例
    const randomIndex = Math.floor(Math.random() * voiceList.length);
    const src = voiceList[randomIndex];
    const cacheKey = `voice_${localeKey}_${normalizedGender}_${type}_${randomIndex}`;
    const audio = getAudio(cacheKey, src, sound.effects);
    audio.volume(sound.effects);
    safePlay(audio, cacheKey);
};